import { FC, ReactNode, ReactText, useCallback, useEffect, useState } from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import { buildUrl } from 'common/util/location';

import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import IconButton from 'components/common/IconButton/IconButton';
import Table from 'components/common/Table/Table';
import Link from 'components/common/Link/Link';
import {
  BudgetCategoryType,
  CostManagementTableRowProps,
  FilterType,
  LogVolumeType,
  OtherLogVolumeType,
} from 'components/billingAndUsage/types';
import { formatBytes, isVolumeLessThanPlanned } from 'components/billingAndUsage/utils';

import './CostManagementTable.scss';

type CostManagementTableProps = {
  budgetCategories: BudgetCategoryType[];
  monthlyVolumes: LogVolumeType[];
  dailyVolumes: LogVolumeType[];
  otherMonthlyVolume: OtherLogVolumeType | null;
  otherDailyVolume: OtherLogVolumeType | null;
  onShowConfirmDialog: (filter: FilterType) => void;
  onDrawerOpen: (filter: FilterType) => void;
  onMove: ({ id, position }: { id: ReactText; position: number }) => void;
  testId?: ELEMENT;
  testIdRow?: ELEMENT;
  testIdFooter?: ELEMENT;
};

export const CostManagementTable: FC<CostManagementTableProps> = ({
  onDrawerOpen,
  budgetCategories,
  dailyVolumes,
  monthlyVolumes,
  onMove,
  onShowConfirmDialog,
  otherDailyVolume,
  otherMonthlyVolume,
  testId,
  testIdRow,
  testIdFooter,
}: CostManagementTableProps) => {
  const [draggableData, setDraggableData] = useState<CostManagementTableRowProps[]>([]);
  const days = new Date().getDate();

  const getLogFileLink = (filter: FilterType): string =>
    buildUrl(K.ROUTE.GRAPH, { [K.URL_PARAM.FILTER]: filter.logFilter } as UrlParams);

  const renderButtons = useCallback(
    (filter: FilterType): JSX.Element => (
      <div className="CostManagementTable__TableIconGroup">
        <Link
          href={getLogFileLink(filter)}
          openInNewTab
          className="CostManagementTable__Table__Icon"
          testId={ELEMENT.planPageOpenSearchForCategoryButton}
        >
          <ElementWithTooltip tooltip="View Logs">
            <SearchIcon />
          </ElementWithTooltip>
        </Link>
        <IconButton
          tooltip="Edit"
          onClick={(): void => onDrawerOpen(filter)}
          icon={<EditIcon />}
          className="CostManagementTable__Table__Icon"
          testId={ELEMENT.planPageEditCategoryButton}
        />
        <IconButton
          tooltip="Delete"
          onClick={(): void => onShowConfirmDialog(filter)}
          icon={<DeleteIcon />}
          className="CostManagementTable__Table__Icon"
          testId={ELEMENT.planPageDeleteCategoryButton}
        />
      </div>
    ),
    [onDrawerOpen, onShowConfirmDialog],
  );

  const renderName = (name: string): JSX.Element => (
    <>
      <DragIndicatorIcon className="CostManagementTable__Table__DragIcon" />
      {name}
    </>
  );

  // Drag-and-drop table data handling
  useEffect(() => {
    const tableData: CostManagementTableRowProps[] = budgetCategories.map((item: BudgetCategoryType) => {
      const filter: FilterType = {
        logFilter: item.logFilter,
        name: item.name,
        plannedGbPerDay: item.plannedGbPerDay,
        id: item.id,
      };
      const monthlyVolume = monthlyVolumes.find(
        (category: LogVolumeType) => category.categoryId.toString() === item.id,
      );
      const dailyVolume = dailyVolumes.find((category: LogVolumeType) => category.categoryId.toString() === item.id);
      const monthlyVolumeBytes = monthlyVolume?.volumeBytes ?? 0;
      const averageMonth = formatBytes(monthlyVolumeBytes === 0 ? 0 : monthlyVolumeBytes / days);
      const averageDay = formatBytes(dailyVolume?.volumeBytes);

      const monthlyVolumeStyle = isVolumeLessThanPlanned(averageMonth, item.plannedGbPerDay)
        ? ''
        : 'CostManagementTable__Table__Item--More';
      const dailyVolumeStyle = isVolumeLessThanPlanned(averageDay, item.plannedGbPerDay)
        ? ''
        : 'CostManagementTable__Table__Item--More';
      return {
        id: item.id,
        name: renderName(item.name),
        plannedGbPerDay: `${item.plannedGbPerDay.toLocaleString()} GB`,
        averageMonth: <span className={monthlyVolumeStyle}>{averageMonth}</span>,
        averageDay: <span className={dailyVolumeStyle}>{averageDay}</span>,
        controls: renderButtons(filter),
        isDraggableRow: true,
      };
    });

    const averageMonthTotal = monthlyVolumes.reduce(
      (sum: number, current: LogVolumeType) => sum + current.volumeBytes,
      0,
    );
    const averageDayTotal = dailyVolumes.reduce((sum: number, current: LogVolumeType) => sum + current.volumeBytes, 0);
    const averageMonth = otherMonthlyVolume?.volumeBytes
      ? (otherMonthlyVolume?.volumeBytes - averageMonthTotal) / days
      : 0;
    const averageDay = otherDailyVolume?.volumeBytes ? otherDailyVolume?.volumeBytes - averageDayTotal : 0;

    const otherDataRow: CostManagementTableRowProps = {
      id: 'other',
      name: 'Other',
      plannedGbPerDay: '',
      averageMonth: <span className="CostManagementTable__Table__Item--Other">{formatBytes(averageMonth)}</span>,
      averageDay: <span className="CostManagementTable__Table__Item--Other">{formatBytes(averageDay)}</span>,
      controls: null,
      isDraggableRow: false,
    };

    tableData.push(otherDataRow);
    setDraggableData(tableData);
  }, [budgetCategories, renderButtons, days, monthlyVolumes, dailyVolumes, otherDailyVolume, otherMonthlyVolume]);

  const getRowId = (row: { [key: string]: string | ReactNode }): string => row.id as string;

  const moveRow = (rows: { [key: string]: string | ReactNode; isDraggableRow?: boolean }[]): void => {
    setDraggableData(rows as CostManagementTableRowProps[]);
  };

  const dropRow = (currentRowId: number | string, position: number): void => {
    const vars = {
      id: currentRowId,
      position,
    };
    onMove(vars);
  };

  const plannedGbPerDayTotal = budgetCategories.reduce(
    (sum: number, current: BudgetCategoryType) => sum + current.plannedGbPerDay,
    0,
  );

  const headers = [
    {
      content: 'Log Category',
      field: 'name',
      footer: 'Total',
    },
    {
      content: 'Planned / Day',
      field: 'plannedGbPerDay',
      alignment: K.ALIGNMENT.END,
      footer: `${plannedGbPerDayTotal.toLocaleString()} GB`,
      width: 135,
    },
    {
      content: 'Average / Day (Month to Date)',
      field: 'averageMonth',
      alignment: K.ALIGNMENT.END,
      footer: formatBytes(otherMonthlyVolume?.volumeBytes ? otherMonthlyVolume?.volumeBytes / days : 0),
      width: 135,
    },
    {
      content: 'Last 24 Hours',
      field: 'averageDay',
      alignment: K.ALIGNMENT.END,
      footer: formatBytes(otherDailyVolume?.volumeBytes),
      width: 135,
    },
    { content: '', field: 'controls', width: 100 },
  ];

  const draggableOptions = {
    isDraggable: true,
    getRowIdCallback: getRowId,
    rowMoveCallback: moveRow,
    rowDropCallback: dropRow,
  };

  const options = {
    noFooters: false,
    headerClass: 'CostManagementTable__Table__Header',
    footerClass: 'CostManagementTable__Table__Footer',
  };

  return (
    <>
      <Table
        headers={headers}
        data={draggableData}
        options={options}
        draggableOptions={draggableOptions}
        testId={testId}
        draggableRowTestId={testIdRow}
        testIdFooter={testIdFooter}
      />
    </>
  );
};
